import React from "react";
import Text from "@ui/Text";
import ButtonIcon from "@ui/ButtonIconV2";
import { CloseLine } from "@ui/IconV2/react";
import { colors, fonts, spacings } from "@ui/Theme";
import t from "../../../../../../../../../i18n";
import tui from "@ui/i18n";
import SidePanel from "../../../../components/SidePanel";
import { css } from "@linaria/core";
import InputV2 from "@ui/Input/InputV2";
import useInput from "@ui/lib/hooks/useInput";
import Select from "@ui/Select";
import { parseAbsolute, type DateValue, type ZonedDateTime } from "@internationalized/date";
import type { FaultEventStatus, PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { getStatusOptions } from "../../../../../../utils/faultEventStatus";
import Button from "@ui/Button";
import { useProjectPeriod } from "../../../../../../../useProjectPeriod";
import type { Project } from "../../../../../../../../../api/useProjects";
import { useRemoteManualFaultEvents } from "../../../../../../../../../api/useManualFaultEvents";
import { NONE_LABEL_FAILURE_STATUS } from "../../../../../../../../../api/useFailureMetadata";
import { buildOptions, NONE_LABEL } from "@ui/Filter";
import { reparabilities, type Reparability } from "../../../../../../../../../api/Reparability";
import { useTranslations } from "../../../../../../../../../api/useTranslations";
import { Markdown } from "@ui/Markdown";
import cn from "classnames";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { usePost, usePut } from "../../../../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { useSettings } from "../../../../../../../../../api/useSettings";
import { DenormalizedEventStatus } from "../../../../../../utils/faultEventStatus";
import { EVENT_PRIORITIES, getEventPriorityText } from "../../../../../../utils/event";
import { useProjectSource } from "../../../../../../../../../api/useSources";
import { useCategories } from "../../../../../../../../../api/useCategories";
import EventCalendar, { timeToDateBoundaries } from "./EventCalendar";
import TextareaInput from "@ui/TextareaInput";
import Field from "@ui/Form/Field";
import {
  validateAction,
  validateClosedAt,
  validateDescription,
  validateName,
  validateStartAt,
  validateNoOverlap,
} from "diagnostics-domain/manualFaultEvent/validations";
type ManualFaultEvent = {
  id: string;
  name: string;
  description: string | null;
  status: DenormalizedEventStatus;
  priority: PriorityFaultEvent;
  reparability: Reparability | null;
  identifiedAction: string | null;
  windows: {
    startsAt: Date;
    endsAt: Date;
  }[];
  startsAt: Date;
  closedAt: Date | null;
  action: string | null;
  fault: {
    id: string;
    systemId: string | null;
  };
};

interface Props {
  project: Project;
  manualFaultEvent: ManualFaultEvent;
  closePanel: () => void;
  isFolded: boolean;
  focusField?: string;
}

const toZonedDateTime = (date: Date | null | undefined, timezone: string): ZonedDateTime | null => {
  if (!date) return null;
  return parseAbsolute(date.toISOString(), timezone);
};

const validateOverlappingEvent = (
  startsAt: Date | null,
  closedAt: Date | null,
  remoteEvents: { startsAt: Date; closedAt: Date | null }[],
) => {
  if (startsAt === null) return;

  const newManualFaultEvent = { startsAt, closedAt };
  return validateNoOverlap(remoteEvents, newManualFaultEvent);
};

const SidePanelEditForm = ({
  isFolded,
  project,
  manualFaultEvent,
  closePanel,
  focusField,
}: Props) => {
  const { addToast } = useToasts();
  const translations = useTranslations();
  const createComment = usePost("/comments");
  const updateFaultManualEvent = usePut("/projects/:projectId(uuid)/manualFaultEvent/:id(uuid)");
  const remoteEvents = useRemoteManualFaultEvents(project.id, true);
  const systems = useCategories("SYSTEM");
  const disableMediumPriority = useSettings().FAULT_EVENTS_DISABLE_MEDIUM_PRIORITY;
  const source = useProjectSource(project);
  if (!source) throw new Error("No source found for the project");
  const [_, __, projectExtremes] = useProjectPeriod({ source, project, boundaries: "analyses" });
  const latestActiveAnalysisDate = React.useMemo(() => {
    const latestActiveAnalysis = project.analysesAggregate?.latestActiveAnalysis?.date;
    if (!latestActiveAnalysis) return null;

    const latestDate = new Date(latestActiveAnalysis);
    latestDate.setHours(23, 59, 59, 999);
    return latestDate;
  }, [project.analysesAggregate?.latestActiveAnalysis?.date]);
  const eventMaxDate = Math.max(
    latestActiveAnalysisDate?.getTime() ?? projectExtremes.from,
    manualFaultEvent.startsAt.getTime(),
    manualFaultEvent.closedAt?.getTime() ?? Number.NEGATIVE_INFINITY,
  );

  const remoteManualFaultEvents = remoteEvents
    .filter(
      event =>
        event.manualFaultId === manualFaultEvent.fault.id && event.id !== manualFaultEvent.id,
    )
    .map(event => ({
      name: event.name,
      startsAt: new Date(event.startsAt),
      closedAt: event.closedAt ? new Date(event.closedAt) : null,
    }));

  const descriptionInput = useInput(manualFaultEvent.description ?? "");
  const handleDescriptionInput = descriptionInput.onChange;
  const errorDescriptionInput = validateDescription({
    changed: descriptionInput.dirty,
    value: descriptionInput.value,
  });

  const nameInput = useInput(manualFaultEvent.name);
  const handleNameInput = nameInput.onChange;
  const errorNameInput = validateName(
    nameInput.value,
    remoteManualFaultEvents.map(e => e.name),
  );

  const originalDefaultPriority = manualFaultEvent.priority;
  const defaultPriority =
    disableMediumPriority && originalDefaultPriority === "MEDIUM"
      ? "HIGH"
      : originalDefaultPriority;

  const priorityOptions = EVENT_PRIORITIES.filter(
    priority => !disableMediumPriority || priority !== "MEDIUM",
  ).map(priority => ({
    label: getEventPriorityText(priority),
    value: priority,
  }));
  const [priority, setPriority] = React.useState(defaultPriority);
  /** @note Force priority regarding to application settings for current event */
  React.useEffect(() => {
    if (disableMediumPriority && priority === "MEDIUM") {
      setPriority("HIGH");
    }
  }, [disableMediumPriority, priority, originalDefaultPriority]);
  const dirtyPriority = defaultPriority !== priority;

  const defaultStatus = manualFaultEvent.status;
  const statusOptions = getStatusOptions(defaultStatus);
  const [status, setStatus] = React.useState(defaultStatus);
  const dirtyStatus = status !== defaultStatus;

  const canEditRepairCategory = status === "ACTION_IDENTIFIED" || status === "CLOSED";

  const defaultRepairCategory =
    status === NONE_LABEL_FAILURE_STATUS || status === "ONGOING"
      ? NONE_LABEL
      : (manualFaultEvent.reparability ?? NONE_LABEL);

  const repairCategoryInput = useInput(defaultRepairCategory);
  const onChangeRepairCategory = repairCategoryInput.onChange;
  const boundaries = timeToDateBoundaries(projectExtremes.from, eventMaxDate, project.timezone);

  const oldStartsAt = parseAbsolute(manualFaultEvent.startsAt.toISOString(), project.timezone);
  const [startsAt, setStartsAt] = React.useState<DateValue | null>(
    parseAbsolute(manualFaultEvent.startsAt.toISOString(), project.timezone),
  );
  const dirtyStartsAt = startsAt?.toString() !== oldStartsAt.toString();
  const oldClosedAt = manualFaultEvent.closedAt
    ? parseAbsolute(manualFaultEvent.closedAt.toISOString(), project.timezone)
    : null;
  const [closedAt, setClosedAt] = React.useState<DateValue | null>(oldClosedAt);
  const dirtyClosedAt = closedAt?.toString() !== oldClosedAt?.toString();
  const firstWindow = toZonedDateTime(manualFaultEvent.windows.at(1)?.startsAt, project.timezone);
  const lastWindow = toZonedDateTime(manualFaultEvent.windows.at(-1)?.startsAt, project.timezone);

  const errorStartsAt = validateStartAt(
    firstWindow?.toDate(),
    startsAt?.toDate(project.timezone),
    closedAt?.toDate(project.timezone),
    boundaries.endBoundary.toDate(),
    boundaries.startBoundary.toDate(),
  );
  const errorClosedAt = validateClosedAt(
    lastWindow?.toDate(),
    closedAt?.toDate(project.timezone),
    startsAt?.toDate(project.timezone),
    boundaries.endBoundary.toDate(),
    boundaries.startBoundary.toDate(),
  );
  const latestStartsAt = Math.min(
    ...([
      closedAt?.toDate(project.timezone)?.getTime(),
      firstWindow?.toDate()?.getTime() ?? eventMaxDate,
    ].filter(v => v !== undefined) as number[]),
  );
  const earliestClosedAt = Math.max(
    ...([
      startsAt?.toDate(project.timezone)?.getTime(),
      lastWindow?.toDate()?.getTime(),
      projectExtremes.from,
    ].filter(v => v !== undefined) as number[]),
  );
  const errorOverlappingManualFaultEvent = validateOverlappingEvent(
    startsAt ? startsAt.toDate(project.timezone) : null,
    closedAt ? closedAt.toDate(project.timezone) : null,
    remoteManualFaultEvents,
  );

  const repairCategoryOptions = [{ label: NONE_LABEL, value: NONE_LABEL }].concat(
    buildOptions(
      reparabilities.map(v => ({
        label: t("reparability", v, translations),
        value: v,
      })),
    ),
  );

  const defaultAction = manualFaultEvent.identifiedAction ?? "";
  const [action, setAction] = React.useState(defaultAction);

  const errorAction = validateAction(action);
  const dirtyAction = defaultAction !== action;

  const [comment, setComment] = React.useState<string>("");
  const defaultSystem = manualFaultEvent.fault.systemId ?? "";
  const [system, setSystem] = React.useState(defaultSystem);
  const dirtySystem = system !== defaultSystem;
  const systemOptions = React.useMemo(
    () =>
      systems.map(systemId => ({
        label: systemId,
        value: systemId,
      })),
    [systems],
  );

  const handleSubmit = async () => {
    const newName = nameInput.value.trim();
    const newPriority = priority;
    const newStartsAt = startsAt;
    if (newStartsAt === null) return false;

    let newAction = status === "CLOSED" ? manualFaultEvent.identifiedAction : action.trim();
    const newDescription = descriptionInput.value.trim();
    let newClosedAt = closedAt;
    let newReparability =
      repairCategoryInput.value === NONE_LABEL ? null : (repairCategoryInput.value as Reparability);
    const newComment = comment.trim();
    let identifiedActionAt: Date | undefined;

    if (status === "ONGOING") {
      newClosedAt = null;
      newAction = null;
      newReparability = null;
    } else if (status === "ACTION_IDENTIFIED") {
      newClosedAt = null;
      identifiedActionAt = new Date();
    }
    try {
      await updateFaultManualEvent({
        body: {
          manualFaultEvent: {
            name: newName,
            description: newDescription,
            startsAt: newStartsAt.toDate(project.timezone),
            closedAt: newClosedAt?.toDate(project.timezone),
            priority: newPriority,
            reparability: newReparability,
            identifiedAction: newAction ?? undefined,
            identifiedActionAt,
          },
          manualFault: {
            systemId: system || null,
          },
        },
        routeParams: { projectId: project.id, id: manualFaultEvent.id },
      });
      if (newComment !== "") {
        await createComment({
          body: {
            type: "RUN",
            projectId: project.id,
            analysisId: null,
            parentId: null,
            content: newComment,
            dashboardId: null,
            blockCommentId: null,
            faultEventId: null,
            manualFaultEventId: manualFaultEvent.id,
          },
        });
      }
      addToast(
        "success",
        t("project.fault_event.evolution_chart.change_fault_event_status.modal.form.toast.success"),
      );
      closePanel();
    } catch {
      addToast("error", t("errors.backend_error"));
    }
    return true;
  };

  const hasChanged = [
    nameInput.dirty,
    descriptionInput.dirty,
    dirtyStatus,
    dirtyStartsAt,
    dirtyClosedAt,
    dirtyPriority,
    repairCategoryInput.dirty,
    dirtyAction,
    dirtySystem,
  ].some(dirty => dirty);

  const isSubmittable = [
    hasChanged,
    !errorNameInput,
    !errorAction,
    !(status === "CLOSED" && closedAt === null),
    startsAt !== null,
    !errorStartsAt,
    !errorClosedAt,
    !errorOverlappingManualFaultEvent,
  ].every(v => v);

  return (
    <SidePanel
      isFolded={isFolded}
      header={
        <>
          <Text type="h4">{t("project.fault_event.side_panel.fault_event_details.title")}</Text>
          <ButtonIcon onClick={closePanel}>
            <CloseLine size="m" color={colors.carbon800} />
          </ButtonIcon>
        </>
      }
    >
      <div className={edit_form}>
        <div className={edit_form_input}>
          <InputV2
            className={featureInput}
            label={t("project.fault_event.side_panel.fault_event_details.input.name")}
            placeholder=""
            value={nameInput.value}
            onChange={handleNameInput}
            error={t("validations.manualFaultEvents", errorNameInput)}
            defaultValue={nameInput.defaultValue}
            revertable={nameInput.dirty}
            required
          />
          <EventCalendar
            label={t("project.events.drawer.create_manual_event.date_picker.startsAt.label")}
            boundaries={{
              startsAt: projectExtremes.from,
              endsAt: latestStartsAt,
            }}
            error={t("validations.manualFaultEvents", errorStartsAt)}
            timezone={project.timezone}
            chosenDate={startsAt}
            onSetChosenDate={setStartsAt}
            isDateUnavailable={(newDate, _, startBoundary, endBoundary) => {
              if (newDate.compare(startBoundary) < 0) return true;
              if (endBoundary && newDate.compare(endBoundary) > 0) return true;
              return false;
            }}
            defaultDate={oldStartsAt}
            disabled={manualFaultEvent.status === "CLOSED"}
          />

          <Field id="" label="Description" className={field}>
            <TextareaInput
              value={descriptionInput.value}
              onChange={handleDescriptionInput}
              error={t("validations.manualFaultEvents", errorDescriptionInput)}
              minHeight={120}
              enforceMaxLength={false}
              showCharCounter={false}
            />
          </Field>
          <Select
            className={featureInput}
            label={t("project.manual_fault_event.edit_form.system.label")}
            prompt={t("common.select")}
            value={system}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSystem(e.target.value)}
            options={systemOptions}
            revertable={dirtySystem}
            revert={() => setSystem(defaultSystem)}
          />
          <AdditionnalMessage
            status="INFO"
            text={t("project.manual_fault_event.edit_form.system.info")}
          />
          <div>
            <Select
              className={featureInput}
              label={t("project.fault_event.side_panel.fault_event_details.select.priority")}
              prompt=""
              defaultValue={defaultPriority}
              value={priority}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setPriority(e.target.value as PriorityFaultEvent)
              }
              options={priorityOptions}
              revertable={dirtyPriority}
              revert={() => setPriority(defaultPriority)}
              clearable={false}
              disabled={manualFaultEvent.status === "CLOSED"}
            />
          </div>

          <Select
            className={featureInput}
            label={t("project.fault_event.modal_form.select.status.label")}
            prompt=""
            defaultValue={defaultStatus}
            value={status}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setStatus(e.target.value as FaultEventStatus)
            }
            options={statusOptions}
            revertable={dirtyStatus}
            revert={() => setStatus(defaultStatus)}
            clearable={false}
          />

          {status === "CLOSED" && (
            <EventCalendar
              label={t("project.events.drawer.create_manual_event.date_picker.closedAt.label")}
              boundaries={{
                startsAt: earliestClosedAt,
                endsAt: eventMaxDate,
              }}
              error={t("validations.manualFaultEvents", errorClosedAt)}
              isEndOfDay
              disabled={manualFaultEvent.status === "CLOSED"}
              timezone={project.timezone}
              chosenDate={closedAt}
              onSetChosenDate={setClosedAt}
              defaultDate={oldClosedAt}
              isDateUnavailable={(newDate, _, startBoundary, endBoundary) => {
                if (startBoundary && newDate.compare(startBoundary) < 0) return true;
                if (endBoundary && newDate.compare(endBoundary) > 0) return true;
                return false;
              }}
            />
          )}

          {canEditRepairCategory && (
            <Select
              className={featureInput}
              label={t("project.fault_event.modal_form.select.outage_type_for_repair.label")}
              prompt=""
              value={repairCategoryInput.value}
              onChange={onChangeRepairCategory}
              options={repairCategoryOptions}
              revertable={repairCategoryInput.dirty}
              revert={() => repairCategoryInput.setValue(defaultRepairCategory)}
              disabled={manualFaultEvent.status === "CLOSED"}
              clearable={false}
            />
          )}

          {(status === "ACTION_IDENTIFIED" || status === "CLOSED") && (
            <div className={markdownWrapper}>
              <Text type="body1SemiBold">
                {t("project.fault_event.modal.form.change_status.input.action_identified.label")}
              </Text>
              {status === "CLOSED" ? (
                <div className={disabledActionMarkdown}>{action}</div>
              ) : (
                <Markdown
                  editable
                  autoFocus={focusField === "actionIdentified"}
                  defaultValue={action}
                  onConversion={m => setAction(m.trim())}
                  placeholder={t(
                    "project.fault_event.modal.form.change_status.input.action_identified.placeholder",
                  )}
                  className={cn(markdownEditorError, {
                    error: t("validations.manualFaultEvents", errorAction),
                  })}
                />
              )}
              {errorAction && (
                <AdditionnalMessage
                  className={markdownError}
                  status="ERROR"
                  text={t("validations.manualFaultEvents", errorAction)}
                />
              )}
            </div>
          )}

          {hasChanged && (
            <div className={markdownWrapper}>
              <Text type="body1SemiBold">
                {t("project.fault_event.modal.form.change_status.comment.label")}
              </Text>
              <Markdown
                editable
                autoFocus={false}
                onConversion={m => setComment(m.trim())}
                defaultValue={comment}
                placeholder={t("project.fault_event.modal.form.change_status.comment.placeholder")}
              />
              <Text type="body2">
                {t("project.fault_event.modal.form.change_status.comment.sub_comment")}
              </Text>
            </div>
          )}
        </div>
      </div>
      <div className={edit_form_submit}>
        <div className={edit_form_submit__mandatory}>
          <sup className={edit_form_submit__sup}>*</sup> {tui("input.mandatory")}
        </div>
        <div className={edit_form_submit__action}>
          <Button onClick={closePanel}> {t("commons.message_card.cancel")}</Button>
          <Button variant="primary" disabled={!isSubmittable} onClick={handleSubmit}>
            {t("commons.button.save")}
          </Button>
        </div>
      </div>
    </SidePanel>
  );
};

export default SidePanelEditForm;

//#region CSS
const edit_form = css`
  flex: 1;
  padding: ${spacings.m};
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const edit_form_input = css`
  width: 100%;
  & > * + * {
    margin-top: ${spacings.m};
  }
`;
const featureInput = css`
  width: 100%;
`;

const markdownWrapper = css`
  & > * + * {
    margin-top: ${spacings.xs};
  }
  color: ${colors.carbon800};
`;
const markdownEditorError = css`
  &.editable {
    &.error {
      border-color: ${colors.oxygen400};
    }
  }
`;
const markdownError = css`
  margin-top: ${spacings.xxs};
`;
const disabledActionMarkdown = css`
  min-height: 34px;
  padding: ${spacings.xs} ${spacings.s};
  border: 1px solid ${colors.titanium200};
  border-radius: 4px;
  background-color: ${colors.titanium50};
  color: ${colors.carbon900};
  font: ${fonts.body1};
  opacity: 0.5;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const field = css`
  width: 100%;

  > div {
    border: unset;
    padding-left: 0;
    width: 100%;

    > div {
      width: 100%;

      div[class*="control"] {
        border-color: ${colors.titanium200};
      }
    }
  }
`;

const edit_form_submit = css`
  position: sticky;
  bottom: 0;
  padding: ${spacings.m};
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.titanium200};
  background-color: ${colors.titanium50};
`;

const edit_form_submit__sup = css`
  color: ${colors.darkNitrogen600};
  vertical-align: bottom;
`;
const edit_form_submit__mandatory = css`
  align-self: flex-end;
  font: ${fonts.body3};
  color: ${colors.darkNitrogen600};
`;
const edit_form_submit__action = css`
  * + * {
    margin-left: ${spacings.xs};
  }
`;
